import { isUnauthorized } from "@/platform/api/errors"
import { sessionGateway, type SessionGateway } from "@/platform/api/gateways/session"
import type { BrowserSessionStatus } from "@/platform/api/session-contract"
import { beginHostedProbe, setHostedSessionVerified } from "@/platform/app-mode/screen-visibility"
import type { SessionProbeResultData, SessionRevokedReason } from "@/platform/bridge/types"
import { browserApi } from "@/platform/session/browser-api"
import { logout } from "@/platform/session/logout"
import { statusOfProbe } from "@/platform/session/probe"
import { claimSession, commitSession, type SessionClaim } from "@/platform/session/publisher"
import { revocationOf } from "@/platform/session/revocation"
import { ANONYMOUS_VIEWER } from "@/platform/session/viewer"

import type { QueryClient } from "@tanstack/react-query"

type HostedSessions = Pick<SessionGateway, "probe" | "redeemHandoff" | "revoke">

type ResolvedStatus = Extract<BrowserSessionStatus, { kind: "authenticated" | "anonymous" }>

const resolveStatus = async (lookup: () => Promise<{ userUuid: string }>, settle: (userUuid: string) => ResolvedStatus): Promise<ResolvedStatus> => {
  try {
    const { userUuid } = await lookup()
    return settle(userUuid)
  } catch (error) {
    if (isUnauthorized(error)) return { kind: "anonymous" }
    throw error
  }
}

export const createHostedSession = (queryClient: QueryClient, sessions: HostedSessions = sessionGateway(browserApi(queryClient, ANONYMOUS_VIEWER))) => {
  let disposed = false
  let revocations = 0
  let revocation: Promise<void> | undefined
  const redeemed = new Set<string>()
  const probing = new Map<string, Promise<SessionProbeResultData>>()

  const lookup = (handoff: string | undefined): Promise<ResolvedStatus> => {
    if (handoff === undefined || redeemed.has(handoff))
      return resolveStatus(
        () => sessions.probe(),
        (userUuid) => statusOfProbe(userUuid, sessions)
      )
    redeemed.add(handoff)
    return resolveStatus(
      () => sessions.redeemHandoff(handoff),
      (userUuid) => ({ kind: "authenticated", userUuid })
    )
  }

  const probe = async (handoff: string | undefined): Promise<SessionProbeResultData> => {
    const endProbe = beginHostedProbe()
    const started = revocations
    let claim: SessionClaim | undefined
    const current = () => !disposed && started === revocations && (claim?.isCurrent() ?? true)
    try {
      if (revocation) await revocation
      if (revocationOf(queryClient) !== "none") throw new Error("Session revocation did not complete")
      claim = claimSession(queryClient)
      const status = await lookup(handoff)
      if (!current() || !(await claim.publish(status)) || !current()) throw new Error("Session changed")
      setHostedSessionVerified(true)
      return status.kind === "authenticated" ? { status: "authenticated", userUuid: status.userUuid } : { status: "anonymous" }
    } catch (error) {
      if (current()) setHostedSessionVerified(false)
      throw error
    } finally {
      endProbe()
    }
  }

  return {
    probeSession: (handoff?: string): Promise<SessionProbeResultData> => {
      if (handoff === undefined) return probe(undefined)
      const shared = probing.get(handoff)
      if (shared) return shared
      const pending = probe(handoff).finally(() => probing.delete(handoff))
      probing.set(handoff, pending)
      return pending
    },
    sessionRevoked: (reason?: SessionRevokedReason) => {
      revocations += 1
      setHostedSessionVerified(false)
      commitSession(queryClient, { kind: "anonymous" })
      if (reason === undefined) return
      const ending = (revocation ??= logout(queryClient, sessions).finally(() => {
        revocation = undefined
      }))
      void ending.catch(() => {})
    },
    dispose: () => {
      disposed = true
      setHostedSessionVerified(false)
    },
  }
}
